import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { isApiError } from '@missions/api-client';
import { appHref } from '../lib/tenant';

/**
 * Designed fallback for anything that would otherwise leave a blank page: tenant config failing for a
 * non-404 reason, a render crash (AppErrorBoundary) or a route error (RouteError). Rendered without app
 * providers, so it only uses plain markup + the base UI classes.
 */
export function ErrorScreen({ title = 'Something went wrong', message, detail, onRetry }: { title?: string; message?: ReactNode; detail?: string | null; onRetry?: () => void }) {
  return (
    <main className="err-screen" role="alert">
      <div className="err-card">
        <img src="/brand/missions-mark.png" alt="" width={44} height={44} />
        <h1>{title}</h1>
        <p>{message ?? 'This page couldn’t be shown. Try again, or sign in again if the problem continues.'}</p>
        <div className="err-acts">
          <button type="button" className="ui-btn primary" onClick={onRetry ?? (() => window.location.reload())}>Try again</button>
          <a className="ui-btn" href={appHref('/login')}>Go to sign in</a>
        </div>
        {detail ? <code>{detail}</code> : null}
      </div>
    </main>
  );
}

/** A lazily loaded page chunk that no longer exists (a new version was deployed while the tab was open). */
export const isStaleChunk = (e: unknown): boolean =>
  e instanceof Error && /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(e.message);

function describe(e: unknown): { title: string; message: string; detail: string | null } {
  if (isStaleChunk(e)) return { title: 'A new version is available', message: 'The app was updated while this tab was open. Reload to continue.', detail: null };
  if (isApiError(e)) return { title: 'Something went wrong', message: e.message || 'The server couldn’t complete that request.', detail: e.requestId ? `Ref ${e.requestId}` : null };
  return { title: 'Something went wrong', message: 'This page couldn’t be shown. Try again, or sign in again if the problem continues.', detail: null };
}

/** Last-resort boundary around the whole app: a crash shows ErrorScreen instead of a blank page. */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  override state: { error: unknown } = { error: null };
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('App crashed', error, info.componentStack);
  }
  override render() {
    if (this.state.error) {
      const d = describe(this.state.error);
      return <ErrorScreen title={d.title} message={d.message} detail={d.detail} />;
    }
    return this.props.children;
  }
}

/** Router errorElement: render/loader errors inside a route (react-router would show its dev page otherwise). */
export function RouteError() {
  const e = useRouteError();
  if (isRouteErrorResponse(e) && e.status === 404) return <ErrorScreen title="Page not found" message="That page doesn’t exist. Check the link, or go back to sign in." onRetry={() => window.history.back()} />;
  const d = describe(e);
  return <ErrorScreen title={d.title} message={d.message} detail={d.detail} />;
}
